import type { CSSProperties } from 'react';

import { ResponsiveImage } from './ResponsiveImage';
import './landingRef.css';

type CropWindow = { top: number; height: number };

type ScreenshotAsset = {
  webp: readonly { src: string; width: number }[];
  fallback: readonly { src: string; width: number }[];
  intrinsic: { w: number; h: number };
  alt: string;
  /** Fractions of the capture's height. Authored in `scripts/import-store-shots.mjs`. */
  crops?: { moment?: CropWindow; plate?: CropWindow };
};

/**
 * How a real app capture is presented.
 *
 * - `moment` — cropped to the one component that carries the claim, held large enough to read.
 *   The default: a whole 9:19.5 screen shrunk to fit a column renders the app's own copy at a size
 *   nobody reads, and that copy is the reason to look at the screenshot at all.
 * - `plate` — the whole screen, frameless, for the few captures whose *layout* is the claim.
 * - `device` — the phone frame. Used once per page, to say "this is a mobile app". It is a category
 *   signal, not decoration, so it does not need repeating.
 */
export type ScreenshotTreatment = 'moment' | 'plate' | 'device';

/**
 * Width tier for a `moment`.
 *
 * A tier rather than a free number because the width feeds the `sizes` hint as well as the CSS box,
 * and a section that narrows a card in its own stylesheet silently makes that hint a lie — the page
 * still works and just fetches a heavier srcset entry than it needs. Sections pick a tier; they do
 * not set widths.
 */
export type MomentWidth = 'lead' | 'default' | 'dense';

type Props = {
  shot: ScreenshotAsset;
  treatment: ScreenshotTreatment;
  /** Width tier — `lead` also lights the card. Ignored by the other treatments. */
  width?: MomentWidth;
  className?: string;
  /** Load immediately — for above-the-fold shots only. */
  priority?: boolean;
};

/** Trims the tab bar, which is the one part of a full screen that reads as "photo of a phone". */
const DEFAULT_PLATE_CROP: CropWindow = { top: 0, height: 0.87 };

/**
 * Rendered width, in one place, because it feeds two things that silently disagree otherwise: the
 * CSS box and the `sizes` hint the browser picks a srcset entry with. When these drift, the page
 * keeps working and just renders soft — so they are derived from the same number.
 */
const SHOT_WIDTH_PX: Record<string, number> = {
  'moment-lead': 372,
  'moment-default': 320,
  /** Fits two side by side in the Signal Fire column without either being capped. */
  'moment-dense': 260,
  plate: 288,
  device: 240,
};

/** Below this the card is capped by its container, so quote a viewport width instead. */
function sizesFor(widthPx: number): string {
  return `(max-width: ${widthPx + 72}px) 90vw, ${widthPx}px`;
}

/** The whole capture. The device frame sizes itself to this rather than cropping into it. */
const WHOLE_CAPTURE: CropWindow = { top: 0, height: 1 };

function cropFor(shot: ScreenshotAsset, treatment: ScreenshotTreatment): CropWindow {
  /**
   * The frame takes the capture's aspect instead of imposing a phone's. A fixed-height screen means
   * `object-fit: cover` trims the sides, and it trims *words* — the app's own copy runs edge to edge,
   * so a few pixels of side crop reads as a rendering bug rather than as a phone.
   */
  if (treatment === 'device') return WHOLE_CAPTURE;
  if (treatment === 'plate') return shot.crops?.plate ?? DEFAULT_PLATE_CROP;
  /**
   * A `moment` without a crop window would otherwise render as a squashed full screen. Falling back
   * to the plate keeps the page correct while the crop is missing, which matters because crops are
   * hand-authored per capture and a newly imported shot may not have one yet.
   */
  return shot.crops?.moment ?? shot.crops?.plate ?? DEFAULT_PLATE_CROP;
}

/**
 * A real in-app capture (from the Playwright store-shots flow), presented without a device frame
 * unless one is asked for.
 */
export function AppScreenshot({
  shot,
  treatment,
  width = 'default',
  className = '',
  priority = false,
}: Props) {
  if (shot.webp.length < 1 && shot.fallback.length < 1) return null;

  const widthKey = treatment === 'moment' ? `moment-${width}` : treatment;
  const widthPx = SHOT_WIDTH_PX[widthKey];
  const crop = cropFor(shot, treatment);

  const windowStyle = {
    aspectRatio: `${shot.intrinsic.w} / ${Math.round(crop.height * shot.intrinsic.h)}`,
    '--mkt-shot-top': `${crop.top}`,
  } as CSSProperties;

  const image = (
    <ResponsiveImage
      webp={[...shot.webp]}
      fallback={[...shot.fallback]}
      sizes={sizesFor(widthPx)}
      width={shot.intrinsic.w}
      height={shot.intrinsic.h}
      alt={shot.alt}
      classNameImage="mkt-shot__img"
      priority={priority}
    />
  );

  const shotWindow = (
    <div className="mkt-shot__window mkt-shimmer" style={windowStyle}>
      {image}
    </div>
  );

  if (treatment === 'device') {
    return (
      <div className={`mkt-phone ${className}`.trim()}>
        <div className="mkt-phone__speaker" aria-hidden />
        <div className="mkt-phone__screen">{shotWindow}</div>
      </div>
    );
  }

  const leadClass = treatment === 'moment' && width === 'lead' ? ' mkt-shot--lead' : '';
  return (
    <div
      className={`mkt-shot mkt-shot--${treatment}${leadClass} ${className}`.trim()}
      style={{ '--mkt-shot-w': `${widthPx}px` } as CSSProperties}>
      {shotWindow}
    </div>
  );
}
